import { useCarConfigSelector } from "../redux/store";
import type { StatusEntryType } from "../../../../common-deprecated/components/modals/status/StatusModalEntry";
import {
    checkAemFlexibilityMatrixFinanceOrInsuranceEnabled,
    getAemFlexibilityMatrixConfig,
    getAemPriceConfig,
    showAemPriceExclVAT,
} from "../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import { RequestStatusEnum } from "../../../../common-deprecated/types/CommonTypes";

type FinanceModalReturnType = {
    entries: StatusEntryType[];
};

const useAphFinanceModal = (): FinanceModalReturnType => {
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const car = useCarConfigSelector((state) => state.car);
    const modelCode = car.model.internalCode;
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const externalPrice = useCarConfigSelector((state) => state.externalPrice);

    const entries: StatusEntryType[] = [
        {
            title: "Finance toggle flags",
            description: "Finance/cash toggles are shown when all flags below are true.",
            values: [
                {
                    label: "Flexibility matrix: finance enabled",
                    value: checkAemFlexibilityMatrixFinanceOrInsuranceEnabled(
                        "finance",
                        commonSettings,
                        AemFmComponent.Carconfig,
                        "new",
                        modelCode,
                    ),
                },
                {
                    label: "Prices are not ExclVAT",
                    value: !showAemPriceExclVAT(commonSettings, AemFmComponent.Carconfig, "new", modelCode),
                },
            ].concat(
                car
                    ? [
                          {
                              label: "showMonthlyRate is available for current car (only applies to engine selection, customize and summary)",
                              value: !car || car?.availability?.showMonthlyRate,
                          },
                      ]
                    : [],
            ),
        },
        {
            title: "Buy toggle flags",
            description: "Buy step is enabled when all flags below are true.",
            values: [{ label: "car.availableForOnlineSales is true", value: car.availableForOnlineSales }],
        },
        {
            title: "Tax and costs flags",
            values: [
                {
                    label: "Country (needs to be fr to show registration tax)",
                    value: commonSettings.country,
                },
                {
                    label: "ACE taxFormats",
                    collapsibleContent: JSON.stringify(carConfigSettings.taxFormats),
                },
                {
                    label: "externalprice calculation is enabled",
                    value: carConfigSettings.enableExternalPrice,
                },
                {
                    label: "externalPrice calculation is working",
                    value: externalPrice.state !== RequestStatusEnum.Error,
                },
            ],
        },
        {
            title: "Finance settings",
            values: [
                {
                    label: "monthlyRateService",
                    value: carConfigSettings.monthlyRateService,
                    long: true,
                },
                {
                    label: "monthlyRateSelectedService",
                    value: carConfigSettings.monthlyRateSelectedService,
                    long: true,
                },
                {
                    label: "monthlyRateUnSelectedService",
                    value: carConfigSettings.monthlyRateUnselectedService,
                    long: true,
                },
                {
                    label: "monthlyRateDisclaimerService",
                    value: carConfigSettings.monthlyRateDisclaimerService,
                    long: true,
                },
                { label: "financeCalculatorUrl", value: carConfigSettings.financeCalculatorUrl, long: true },
                {
                    label: "showAccessoriesRateCarConfig",
                    value: carConfigSettings.showAccessoriesRate,
                },
            ],
        },
        {
            title: "Insurance settings",
            values: [
                {
                    label: "monthlyRateService",
                    value: carConfigSettings.insuranceRate,
                },
                { label: "insuranceModuleURL", value: carConfigSettings.insuranceModuleURL },
                {
                    label: "Flexibility matrix: insurance enabled",
                    value: checkAemFlexibilityMatrixFinanceOrInsuranceEnabled(
                        "insurance",
                        commonSettings,
                        AemFmComponent.Carconfig,
                        "new",
                        modelCode,
                    ),
                },
            ],
        },
        {
            title: "Monthly rate availability",
            values: car.availability
                ? [
                      { label: "ShowAccessoriesRateCarConfig", value: car.availability.showAccessoriesRateCarConfig },
                      { label: "ShowAnnualInterestRate", value: car.availability.showAnnualInterestRate },
                      { label: "ShowEffectiveInterestRate", value: car.availability.showEffectiveInterestRate },
                      { label: "ShowFinancialButton", value: car.availability.showFinanceButton },
                      { label: "ShowInsuranceButton", value: car.availability.showInsuranceButton },
                      { label: "ShowMonthlyRate", value: car.availability.showMonthlyRate },
                      { label: "ShowProductName", value: car.availability.showProductName },
                      { label: "ShowTerm", value: car.availability.showTerm },
                      { label: "ShowInsurancePremiumCarConfig", value: car.availability.showInsurancePremiumCarConfig },
                  ]
                : [],
        },
        {
            title: "AEM Flexibility matrix settings",
            values: [
                {
                    label: "component",
                    value: getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Carconfig, "new", modelCode)
                        ?.component as string,
                },
                {
                    label: "last updated",
                    value: getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Carconfig, "new", modelCode)
                        ?.lastModified,
                },
                { label: "model code", value: modelCode },
                {
                    label: "getPriceConfig priceFormat",
                    value: getAemPriceConfig(commonSettings, AemFmComponent.Carconfig, modelCode).priceFormat,
                },
                {
                    label: "getPriceConfig priceType",
                    value: getAemPriceConfig(commonSettings, AemFmComponent.Carconfig, modelCode).priceType as string,
                },
                {
                    label: "showPriceExcl",
                    value: showAemPriceExclVAT(commonSettings, AemFmComponent.Carconfig, "new", modelCode),
                },
                {
                    label: "Parsed AEM Flexibility Matrix Config",
                    collapsibleContent: JSON.stringify(
                        getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Carconfig, "new", modelCode),
                        undefined,
                        2,
                    ),
                },
            ],
        },
    ];

    return {
        entries,
    };
};

export default useAphFinanceModal;
